import React, { createContext, useContext, useState, useMemo } from 'react';
import Dialog from '../components/Dialog/Dialog';
import { PrimaryButton, TertiaryButton } from '../components/AtomComponents';

export interface ConfirmOptions {
  title?: string;
  content?: React.ReactNode | string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
}

interface ConfirmContextType {
  confirm: (options: ConfirmOptions) => void;
}

const ConfirmContext = createContext<ConfirmContextType | undefined>(undefined);

export const ConfirmProvider: React.FC = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmOptions | null>(null);

  const confirm = (opts: ConfirmOptions) => {
    setOptions(opts);
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setOptions(null);
  };

  const handleConfirm = () => {
    if (options && options.onConfirm) {
      options.onConfirm();
    }
    handleClose();
  };

  const value = useMemo(() => ({ confirm }), []);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      {options && (
        <Dialog
          isDialogOpen={isOpen}
          closeDialog={handleClose}
          title={options.title || ''}
          content={options.content || 'Do you really want to proceed with this action?'}
          actions={
            <>
              <TertiaryButton onClick={handleClose}>
                {options.cancelText || 'CANCEL'}
              </TertiaryButton>
              <PrimaryButton onClick={handleConfirm}>
                {options.confirmText || 'PROCEED'}
              </PrimaryButton>
            </>
          }
        />
      )}
    </ConfirmContext.Provider>
  );
};

export const useConfirm = () => {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }
  return context;
};
